import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  User, 
  Plus, 
  X, 
  Lock, 
  Unlock,
  Trash2,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { API_BASE } from '@/config/api';
import { useAuth } from '@/context/AuthContext';

interface CalendarSlot {
  id: number;
  date: string | Date;
  start_time: string;
  end_time: string;
  status: 'available' | 'booked' | 'blocked';
  service_type?: string;
  notes?: string;
  appointment?: {
    id: number;
    customer_name: string;
    customer_email: string;
    service_type: string;
    notes?: string;
  };
}

interface Appointment {
  id: number;
  customer_name: string;
  customer_email: string;
  tailor_name: string;
  slot_id?: number;
  date: string | Date;
  start_time: string;
  end_time: string;
  service_type: string;
  status: string;
  notes?: string;
  customer_notes?: string;
  created_at: string | Date;
}

const TailorCalendarManager: React.FC = () => {
  const { user } = useAuth();
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
  
  // Form state for creating slots
  const [newSlot, setNewSlot] = useState({
    date: '',
    start_time: '09:00',
    end_time: '10:00',
    service_type: 'consultation',
    notes: ''
  });

  useEffect(() => {
    if (user) {
      fetchCalendarData();
    }
  }, [user, selectedDate]);

  const fetchCalendarData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // Calculate date range (current month)
      const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      
      const dateFrom = startDate.toISOString().split('T')[0];
      const dateTo = endDate.toISOString().split('T')[0];

      // Fetch calendar slots
      const slotsResponse = await fetch(
        `${API_BASE}/api/appointments/calendar-slots?date_from=${dateFrom}&date_to=${dateTo}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!slotsResponse.ok) {
        throw new Error(`Failed to fetch calendar slots: ${slotsResponse.statusText}`);
      }

      const slotsData = await slotsResponse.json();
      
      // Fetch appointments to get customer booking information
      const appointmentsResponse = await fetch(
        `${API_BASE}/api/appointments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let appointmentsData: Appointment[] = [];
      if (appointmentsResponse.ok) {
        appointmentsData = await appointmentsResponse.json();
        setAppointments(appointmentsData);
      }

      // Merge appointments with slots
      const slotsWithAppointments = (slotsData || []).map((slot: any) => {
        // Find appointment that matches this slot by slot_id or by date and time
        const appointment = appointmentsData.find((apt: Appointment) => {
          if (apt.slot_id && slot.id) {
            return apt.slot_id === slot.id;
          }
          // Fallback: match by date and time
          const aptDate = typeof apt.date === 'string' ? apt.date.split('T')[0] : 
                         apt.date instanceof Date ? apt.date.toISOString().split('T')[0] :
                         apt.date?.toString().split('T')[0];
          const slotDate = typeof slot.date === 'string' ? slot.date.split('T')[0] : 
                          slot.date instanceof Date ? slot.date.toISOString().split('T')[0] :
                          slot.date?.toString().split('T')[0];
          return aptDate === slotDate && 
                 apt.start_time === slot.start_time && 
                 apt.end_time === slot.end_time;
        });
        
        return {
          ...slot,
          appointment: appointment ? {
            id: appointment.id,
            customer_name: appointment.customer_name || 'Unknown Customer',
            customer_email: appointment.customer_email || '',
            service_type: appointment.service_type || 'consultation',
            notes: appointment.notes
          } : undefined
        };
      });

      setSlots(slotsWithAppointments);
    } catch (err: any) {
      console.error('Error fetching calendar data:', err);
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSlot = async () => {
    if (!user || !newSlot.date) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/appointments/calendar-slots`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: newSlot.date,
          start_time: newSlot.start_time,
          end_time: newSlot.end_time,
          service_type: newSlot.service_type,
          notes: newSlot.notes,
          status: 'available'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create slot');
      }

      setShowCreateModal(false);
      setNewSlot({
        date: '',
        start_time: '09:00',
        end_time: '10:00',
        service_type: 'consultation',
        notes: ''
      });
      fetchCalendarData();
    } catch (err: any) {
      setError(err.message || 'Failed to create slot');
    }
  };

  const handleBlockSlot = async (slotId: number, block: boolean) => {
    if (!user) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/appointments/calendar-slots/${slotId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: block ? 'blocked' : 'available'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update slot');
      }

      fetchCalendarData();
      setShowBlockModal(false);
      setSelectedSlot(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update slot');
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    if (!user) return;
    if (!confirm('Are you sure you want to delete this slot? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE}/api/appointments/calendar-slots/${slotId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete slot');
      }

      fetchCalendarData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete slot');
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const getSlotsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return slots.filter(slot => {
      // Handle both string and Date formats
      const slotDate = typeof slot.date === 'string' 
        ? slot.date.split('T')[0] 
        : slot.date instanceof Date 
        ? slot.date.toISOString().split('T')[0]
        : new Date(slot.date).toISOString().split('T')[0];
      return slotDate === dateStr;
    });
  };

  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calendar Management</h2>
          <p className="text-gray-600 mt-1">Manage your availability and view customer bookings</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Slot</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Month Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
              {day}
            </div>
          ))}

          {/* Calendar Days */}
          {getDaysInMonth().map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="aspect-square" />;
            }

            const daySlots = getSlotsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();
            const availableCount = daySlots.filter(s => s.status === 'available').length;
            const bookedCount = daySlots.filter(s => s.status === 'booked').length;
            const blockedCount = daySlots.filter(s => s.status === 'blocked').length;

            return (
              <div
                key={date.toISOString()}
                className={`aspect-square border rounded-lg p-2 cursor-pointer hover:bg-gray-50 transition-colors ${
                  isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
                onClick={() => {
                  setNewSlot(prev => ({ ...prev, date: date.toISOString().split('T')[0] }));
                  setShowCreateModal(true);
                }}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                  {date.getDate()}
                </div>
                {daySlots.length > 0 && (
                  <div className="space-y-1">
                    {availableCount > 0 && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-xs text-gray-600">{availableCount}</span>
                      </div>
                    )}
                    {bookedCount > 0 && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span className="text-xs text-gray-600">{bookedCount}</span>
                      </div>
                    )}
                    {blockedCount > 0 && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-xs text-gray-600">{blockedCount}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Slots List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Slots</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {slots.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No calendar slots found for this month.</p>
              <p className="text-sm mt-2">Click "Create Slot" to add availability.</p>
            </div>
          ) : (
            slots.map(slot => (
              <div
                key={slot.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        slot.status === 'available' ? 'bg-green-500' :
                        slot.status === 'booked' ? 'bg-blue-500' :
                        'bg-red-500'
                      }`} />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">
                            {(() => {
                              const slotDate = typeof slot.date === 'string' 
                                ? new Date(slot.date) 
                                : slot.date instanceof Date 
                                ? slot.date 
                                : new Date(slot.date);
                              return slotDate.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              });
                            })()}
                          </span>
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {slot.start_time} - {slot.end_time}
                          </span>
                        </div>
                        {slot.appointment && (
                          <div className="mt-2 flex items-center space-x-2 text-sm text-blue-600">
                            <User className="w-4 h-4" />
                            <span>
                              Booked by: {slot.appointment.customer_name} ({slot.appointment.customer_email})
                            </span>
                          </div>
                        )}
                        {slot.notes && (
                          <p className="text-sm text-gray-500 mt-1">{slot.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {slot.status === 'available' && (
                      <button
                        onClick={() => {
                          setSelectedSlot(slot);
                          setShowBlockModal(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Block slot"
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                    )}
                    {slot.status === 'blocked' && (
                      <button
                        onClick={() => handleBlockSlot(slot.id, false)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Unblock slot"
                      >
                        <Unlock className="w-4 h-4" />
                      </button>
                    )}
                    {slot.status !== 'booked' && (
                      <button
                        onClick={() => handleDeleteSlot(slot.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete slot"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Slot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Calendar Slot</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={newSlot.date}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={newSlot.start_time}
                    onChange={(e) => setNewSlot(prev => ({ ...prev, start_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={newSlot.end_time}
                    onChange={(e) => setNewSlot(prev => ({ ...prev, end_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
                <select
                  value={newSlot.service_type}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, service_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="consultation">Consultation</option>
                  <option value="fitting">Fitting</option>
                  <option value="measurement">Measurement</option>
                  <option value="delivery">Delivery</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={newSlot.notes}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add any notes about this slot..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateSlot}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Slot
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Block Slot Confirmation Modal */}
      {showBlockModal && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Block Calendar Slot</h3>
              <button
                onClick={() => {
                  setShowBlockModal(false);
                  setSelectedSlot(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to block this slot? Customers will not be able to book it.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-600">
                <strong>Date:</strong> {(() => {
                  const slotDate = typeof selectedSlot.date === 'string' 
                    ? new Date(selectedSlot.date) 
                    : selectedSlot.date instanceof Date 
                    ? selectedSlot.date 
                    : new Date(selectedSlot.date);
                  return slotDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  });
                })()}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <strong>Time:</strong> {selectedSlot.start_time} - {selectedSlot.end_time}
              </p>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBlockModal(false);
                  setSelectedSlot(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleBlockSlot(selectedSlot.id, true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Block Slot
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TailorCalendarManager;

